// Packages
import {
  AttachInternals,
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Prop,
  Watch,
} from "@stencil/core";

// Types
import { TThemeColors } from "src/interface";
import { IToggleChangeEventDetail } from "src/interface";

// Utils
import { buildColorClasses } from "@utils/theme";

/**
 * `fx-toggle` is a headless, accessible form-associated switch component.
 * It exposes every styleable surface via `part` attributes and its state
 * through `data-*` and ARIA attributes so themes can customize it without
 * internal styles.
 *
 * @slot - The toggle label.
 *
 * @part native - The focusable switch button.
 * @part track - The switch track background.
 * @part handle - The switch handle.
 * @part label - The wrapper around the default slot.
 */
// `styleUrl` is intentionally omitted (no internal styles per AGENTS.md).
@Component({
  tag: "fx-toggle",
  shadow: true,
  formAssociated: true,
})
export class FxToggle {
  // --------------------------------------------
  // Element / internal references
  // --------------------------------------------

  @Element() el!: HTMLElement;

  @AttachInternals() internals?: ElementInternals;

  // --------------------------------------------
  // Props
  // --------------------------------------------

  /**
   * @prop - If `true`, the toggle is checked. `mutable` so the component can
   * update it on toggle.
   */
  @Prop({ mutable: true })
  checked?: boolean;

  /**
   * @prop - If `true`, the toggle is disabled and cannot be interacted with.
   * Reflects to the `disabled` content attribute, `data-disabled` and
   * `aria-disabled`.
   */
  @Prop({ reflect: true })
  disabled?: boolean;

  /**
   * @prop - If `true`, the toggle is read-only and cannot be toggled.
   * Reflects to `data-readonly`.
   */
  @Prop()
  readonly?: boolean;

  /**
   * @prop - If `true`, the toggle is required. Reflects to the `required`
   * content attribute, `data-required` and `aria-required`.
   */
  @Prop({ reflect: true })
  required?: boolean;

  /**
   * @prop - The name of the toggle, used when submitting the form.
   */
  @Prop()
  name?: string;

  /**
   * @prop - The value of the toggle submitted with the form when checked.
   * Defaults to `'on'`.
   */
  @Prop()
  value?: string;

  /**
   * @prop - The color theme of the toggle. Applies `fx-color-{color}` and
   * `fx-color-theme` classes on the host and reflects to `data-color`.
   * Defaults to `primary`.
   */
  @Prop()
  color?: TThemeColors;

  // --------------------------------------------
  // Events
  // --------------------------------------------

  /**
   * @event - Emitted when the toggle receives focus.
   */
  @Event()
  fxFocus!: EventEmitter<FocusEvent>;

  /**
   * @event - Emitted when the toggle loses focus.
   */
  @Event()
  fxBlur!: EventEmitter<FocusEvent>;

  /**
   * @event - Emitted when the checked state changes, with detail `{ value, checked }`.
   */
  @Event()
  fxChange!: EventEmitter<IToggleChangeEventDetail>;

  // --------------------------------------------
  // Lifecycle
  // --------------------------------------------

  componentWillLoad() {
    this.syncFormValue();
  }

  // --------------------------------------------
  // Watchers
  // --------------------------------------------

  @Watch("checked")
  @Watch("value")
  @Watch("disabled")
  @Watch("required")
  formStateChanged() {
    this.syncFormValue();
  }

  // --------------------------------------------
  // Private getters
  // --------------------------------------------

  private get isChecked() {
    return this.checked ?? false;
  }

  private get isDisabled() {
    return this.disabled ?? false;
  }

  private get isReadonly() {
    return this.readonly ?? false;
  }

  private get isRequired() {
    return this.required ?? false;
  }

  private get effectiveValue() {
    return this.value ?? "on";
  }

  private get effectiveColor(): TThemeColors {
    return this.color ?? "primary";
  }

  private get hostAriaLabel(): string | null {
    return this.el.getAttribute("aria-label") || null;
  }

  // --------------------------------------------
  // Private handlers
  // --------------------------------------------

  private handleClick = (ev: MouseEvent) => {
    if (this.isDisabled || this.isReadonly) {
      ev.preventDefault();
      return;
    }

    this.toggle();
  };

  private handleKeyDown = (ev: KeyboardEvent) => {
    if (this.isDisabled || this.isReadonly) {
      return;
    }

    if (ev.key === " " || ev.key === "Spacebar" || ev.key === "Enter") {
      ev.preventDefault();
      this.toggle();
    }
  };

  private handleFocus = (ev: FocusEvent) => {
    if (this.isDisabled) {
      return;
    }

    this.fxFocus.emit(ev);
  };

  private handleBlur = (ev: FocusEvent) => {
    this.fxBlur.emit(ev);
  };

  // --------------------------------------------
  // Private helpers
  // --------------------------------------------

  private toggle() {
    const previous = this.isChecked;

    this.checked = !previous;

    if (this.checked !== previous) {
      this.emitChange();
    }
  }

  private emitChange() {
    this.fxChange.emit({
      value: this.effectiveValue,
      checked: this.isChecked,
    });
  }

  private syncFormValue() {
    if (!this.internals) {
      return;
    }

    if (this.isDisabled) {
      if (typeof this.internals.setFormValue === "function") {
        this.internals.setFormValue(null);
      }
    } else if (typeof this.internals.setFormValue === "function") {
      this.internals.setFormValue(this.isChecked ? this.effectiveValue : null);
    }

    if (typeof this.internals.setValidity === "function") {
      const isInvalid = this.isRequired && !this.isChecked;
      this.internals.setValidity({ valueMissing: isInvalid }, isInvalid ? "Required" : "");
    }
  }

  // --------------------------------------------
  // Render
  // --------------------------------------------

  render() {
    const {
      isChecked,
      isDisabled,
      isReadonly,
      isRequired,
      effectiveColor,
      hostAriaLabel,
      name,
    } = this;

    return (
      <Host
        name={name || undefined}
        data-checked={isChecked ? "" : undefined}
        data-disabled={isDisabled ? "" : undefined}
        data-readonly={isReadonly ? "" : undefined}
        data-required={isRequired ? "" : undefined}
        data-color={effectiveColor}
        class={buildColorClasses(effectiveColor)}
      >
        <button
          part="native"
          type="button"
          role="switch"
          aria-label={hostAriaLabel}
          aria-checked={isChecked ? "true" : "false"}
          aria-disabled={isDisabled ? "true" : null}
          aria-readonly={isReadonly ? "true" : null}
          aria-required={isRequired ? "true" : null}
          disabled={isDisabled}
          tabIndex={isDisabled ? -1 : 0}
          onClick={this.handleClick}
          onKeyDown={this.handleKeyDown}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
        >
          <span part="track" aria-hidden="true">
            <span part="handle" aria-hidden="true" />
          </span>
          <span part="label">
            <slot />
          </span>
        </button>
      </Host>
    );
  }
}
